import { ReactNode, useCallback, useState } from "react"
import { makeStyles } from "tss-react/mui"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { Accordion, AccordionSummary, AccordionDetails, Typography, Box, Stack } from "@mui/material"

import { LocalizedStrict } from "../../../modules/localization/components/AppLocalized"
import { StandardButton } from "../buttons/StandardButton"
import { ExternalLink } from "../links/ExternalLink"
import { StandardDialog } from "./StandardDialog"

const useStyles = makeStyles()((theme) => ({
    container: {
        padding: "5rem",
        paddingTop: "1rem",
    },
    description: {
        color: theme.palette.text.primary,
    },
    supportText: {
        color: theme.palette.text.primary,
    },
    supportLink: {
        textDecoration: "none",
    },
    accordion: {
        boxShadow: "none",
        "&:before": {
            display: "none",
        },
        ".MuiAccordionSummary-root": {
            padding: 0,
            minHeight: "2.75rem",
        },
    },
    accordionDetails: {
        padding: "0.375rem 0",
    },
    detailsText: {
        fontSize: "0.85rem",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        margin: 0,
        color: theme.palette.text.secondary,
    },
    buttonContainer: {
        marginTop: "4rem",
        display: "flex",
        justifyContent: "center",
    },
    closeButton: {
        minWidth: "6.125rem",
        fontSize: "0.875rem",
        padding: "1.063rem 2rem",
        borderRadius: "0.5rem",
    },
}))

type StandardErrorDialogState = Readonly<{
    open: boolean
    title: string
    description: string
}>

const defaultStandardErrorDialogState: StandardErrorDialogState = {
    open: false,
    title: "",
    description: "",
}

export function useStandardErrorDialogState() {
    const [errorDialog, setErrorDialog] = useState<StandardErrorDialogState>(defaultStandardErrorDialogState)

    const openErrorDialog = useCallback((args: Readonly<Omit<StandardErrorDialogState, "open">>) => {
        setErrorDialog({
            open: true,
            ...args,
        })
    }, [])

    const closeErrorDialog = useCallback(() => {
        setErrorDialog(defaultStandardErrorDialogState)
    }, [])

    return {
        errorDialog,
        setErrorDialog,
        openErrorDialog,
        closeErrorDialog,
    }
}

type ErrorModalProps = Readonly<{
    dialogDetails: StandardErrorDialogState
    onClose: () => void
    details?: string | ReactNode
}>

export function StandardErrorDialog({ dialogDetails, onClose, details }: ErrorModalProps) {
    const { classes } = useStyles()
    const { open, title, description } = dialogDetails

    return (
        <StandardDialog
            open={open}
            onClose={onClose}
            maxWidthProp="sm"
            dialogTitle={
                !!title ? (
                    title
                ) : (
                    <LocalizedStrict id="app-error-boundary-title">
                        <>Something went wrong</>
                    </LocalizedStrict>
                )
            }
        >
            <Box className={classes.container}>
                <Stack gap="1.5rem">
                    {!!description && (
                        <Typography variant="body1" className={classes.description}>
                            {description}
                        </Typography>
                    )}

                    {/* Standard support contact message */}
                    <LocalizedStrict
                        id="error-dialog-support-request"
                        elems={{
                            supportMessage: (
                                <ExternalLink href={`mailto:support@facilitynet.dk`} className={classes.supportLink}>
                                    support@facilitynet.dk
                                </ExternalLink>
                            ),
                        }}
                    >
                        <Typography>
                            Please contact{" "}
                            <ExternalLink href={`mailto:support@facilitynet.dk`} className={classes.supportLink}>
                                support@facilitynet.dk
                            </ExternalLink>{" "}
                            if the problem persists.
                        </Typography>
                    </LocalizedStrict>

                    {/* Optional collapsible details accordion */}
                    {details && (
                        <Accordion className={classes.accordion} disableGutters>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <LocalizedStrict id="error-dialog-show-details-label">
                                    <Typography>Show details</Typography>
                                </LocalizedStrict>
                            </AccordionSummary>
                            <AccordionDetails className={classes.accordionDetails}>
                                {typeof details === "string" ? (
                                    <Typography variant="body2" className={classes.detailsText}>
                                        {details}
                                    </Typography>
                                ) : (
                                    details
                                )}
                            </AccordionDetails>
                        </Accordion>
                    )}
                </Stack>

                <Box className={classes.buttonContainer}>
                    <LocalizedStrict id="error-dialog-close-button">
                        <StandardButton
                            variant="contained"
                            fullWidth={false}
                            onClick={onClose}
                            className={classes.closeButton}
                        >
                            Close
                        </StandardButton>
                    </LocalizedStrict>
                </Box>
            </Box>
        </StandardDialog>
    )
}
